import { z } from 'zod';
import { genderOptions, raceOptions } from '@/data/commonDataOptions';
import { cpfValido } from '../utils/masks';
import {
  BANCO_ITAU_CODIGO,
  ESCOLARIDADE_OPCOES,
  ESCOLARIDADE_STATUS_OPCOES,
  SEXO_OPCOES,
  escolaridadeTemCurso,
} from './candidato.opcoes';

export const RespostasEtapa1CandidatoSchema = z.object({
  nomeCompleto: z.string().nullable(),
  dataNascimento: z.string().nullable(),
  rg: z.string().nullable(),
  orgaoEmissorRg: z.string().nullable(),
  orgaoEmissorRgUf: z.string().nullable(),
  dataEmissaoRg: z.string().nullable().optional(),
  cpf: z.string().nullable(),
  cidadeNascimento: z.string().nullable().optional(),
  ufNascimento: z.string().nullable().optional(),
  cidadeEstadoNascimento: z.string().nullable(),
  cidadeEstadoResidencia: z.string().nullable(),
  nomeMae: z.string().nullable(),
  nomePai: z.string().nullable(),
  sobreIaEtapas: z.string().nullable(),
  sobreIaProcesso: z.string().nullable(),
  sobreIaQualidade: z.string().nullable(),
  disponibilidadeInicio: z.string().nullable(),
  pretensaoSalarial: z.string().nullable(),
  autorizacaoLgpd: z.boolean(),
});

export const CandidatoCreateSchema = z.object({
  nome: z.string().trim().min(1, 'Nome é obrigatório'),
  email: z.string().trim().email('E-mail inválido'),
  perguntasSobreIA: z.boolean(),
  createdBy: z.object({
    id: z.string().min(1),
    name: z.string().min(1),
  }),
});

/** Espelha exatamente RespostasEtapa1CandidatoSchema — validado tanto no formulário
 * externo (client) quanto na Cloud Function `submitCandidatoExterno` (server). */
export const SubmeterRespostasEtapa1Schema = RespostasEtapa1CandidatoSchema.extend({
  cidadeNascimento: z.string().trim().min(1, 'Informe a cidade de nascimento.'),
  ufNascimento: z.string().trim().min(1, 'Informe o estado (UF) de nascimento.'),
  autorizacaoLgpd: z.literal(true, {
    errorMap: () => ({ message: 'É necessário autorizar o uso dos dados para continuar.' }),
  }),
}).refine((d) => cpfValido(d.cpf), { message: 'CPF inválido — confira os números.', path: ['cpf'] });

const FilhoEtapa2Schema = z.object({
  nome: z.string().trim().min(1, 'Nome do filho é obrigatório'),
  dataNascimento: z.string().trim().min(1, 'Data de nascimento do filho é obrigatória'),
  sexo: z.enum(SEXO_OPCOES, { errorMap: () => ({ message: 'Informe o sexo do filho.' }) }),
  incluirNoPlano: z.boolean().optional(),
  certidaoNascimentoUrl: z.string().nullable().optional(),
  rgIdentidadeUrl: z.string().nullable().optional(),
  documentoUrl: z.string().nullable().optional(),
});

const DependentePlanoSaudeEtapa2Schema = z.object({
  nome: z.string().trim().min(1, 'Nome do dependente é obrigatório'),
  parentesco: z.string().trim().min(1, 'Parentesco do dependente é obrigatório'),
  idade: z.string().trim().min(1, 'Idade do dependente é obrigatória'),
});

const DocumentosEtapa2Schema = z.object({
  identidadeFrenteUrl: z.string().nullable(),
  identidadeVersoUrl: z.string().nullable(),
  comprovanteEnderecoUrl: z.string().nullable(),
  fotoRecenteUrl: z.string().nullable(),
  certidaoCasamentoUrl: z.string().nullable(),
  rgConjugeUrl: z.string().nullable(),
  rgMaeUrl: z.string().nullable().optional(),
  rgPaiUrl: z.string().nullable().optional(),
  laudoPcdUrl: z.string().nullable().optional(),
});

/** Validação de RespostasEtapa2Candidato — os `.refine()` cobrem os campos
 * condicionais (só exigidos quando a pergunta-gatilho manda mostrá-los), mesma lista de
 * condicionais acertada na conversa com o RH. */
export const RespostasEtapa2CandidatoSchema = z
  .object({
    // Mesmas listas fixas usadas no perfil do colaborador (PersonalDataFieldset) — evita
    // que um texto livre aqui não bata com o enum de `race` no perfil na hora de aplicar.
    genero: z.enum(genderOptions as [string, ...string[]], { errorMap: () => ({ message: 'Selecione uma opção.' }) }),
    raca: z.enum(raceOptions as [string, ...string[]], { errorMap: () => ({ message: 'Selecione uma opção.' }) }),
    sexo: z.enum(SEXO_OPCOES, { errorMap: () => ({ message: 'Informe o sexo (conforme documento).' }) }),
    orgaoEmissorRg: z.string().trim().min(1, 'Informe o órgão emissor do RG.'),
    orgaoEmissorRgUf: z.string().trim().min(1, 'Informe a UF do órgão emissor do RG.'),
    dataEmissaoRg: z.string().trim().min(1, 'Informe a data de emissão do RG.'),
    sobrenomeEmail: z.string().trim().min(1, 'Campo obrigatório'),
    nacionalidade: z.string().trim().min(1, 'Campo obrigatório'),
    estadoCivil: z.string().trim().min(1, 'Campo obrigatório'),
    nomeConjuge: z.string().nullable(),
    possuiDeficiencia: z.boolean(),
    deficienciaCid: z.string().nullable(),
    escolaridade: z.enum(ESCOLARIDADE_OPCOES, { errorMap: () => ({ message: 'Selecione a escolaridade.' }) }),
    escolaridadeStatus: z.enum(ESCOLARIDADE_STATUS_OPCOES, {
      errorMap: () => ({ message: 'Informe se está concluído, em andamento ou trancado.' }),
    }),
    escolaridadeCurso: z.string().nullable(),
    telefone: z.string().trim().min(1, 'Campo obrigatório'),
    emailPessoal: z.string().trim().email('E-mail inválido'),
    contatoEmergenciaNome: z.string().trim().min(1, 'Campo obrigatório'),
    contatoEmergenciaTelefone: z.string().trim().min(1, 'Campo obrigatório'),
    contatoEmergenciaParentesco: z.string().trim().min(1, 'Campo obrigatório'),
    endereco: z.string().trim().min(1, 'Campo obrigatório'),
    enderecoCep: z.string().trim().regex(/^\d{5}-?\d{3}$/, 'CEP inválido.'),
    enderecoLogradouro: z.string().trim().min(1, 'Informe a rua.'),
    enderecoNumero: z.string().trim().min(1, 'Informe o número.'),
    enderecoComplemento: z.string().nullable().optional(),
    enderecoBairro: z.string().trim().min(1, 'Informe o bairro.'),
    enderecoCidade: z.string().trim().min(1, 'Informe a cidade.'),
    enderecoUf: z.string().trim().min(1, 'Informe o estado (UF).'),
    enderecoPais: z.string().trim().min(1, 'Informe o país.'),
    tipoContrato: z.string().trim().min(1, 'Campo obrigatório'),
    cnpj: z.string().nullable(),
    razaoSocial: z.string().nullable().optional(),
    estagioPeriodo: z.string().nullable(),
    estagioInstituicao: z.string().nullable(),
    possuiContaItau: z.boolean(),
    outroBancoCodigo: z.string().nullable().optional(),
    outroBancoAgencia: z.string().nullable(),
    outroBancoConta: z.string().nullable(),
    outroBancoDigito: z.string().nullable().optional(),
    outroBancoPix: z.string().nullable(),
    regimeTrabalho: z.array(z.string()).min(1, 'Selecione ao menos uma opção.'),
    linhaOnibusTarifa: z.string().nullable(),
    planoSaude: z.string().trim().min(1, 'Campo obrigatório'),
    incluirConjugePlano: z.boolean().optional(),
    // Formato antigo — o formulário atual sempre manda false / [] (ver incluirConjugePlano
    // e filhos[].incluirNoPlano).
    desejaDependentesPlano: z.boolean(),
    dependentesPlano: z.array(DependentePlanoSaudeEtapa2Schema),
    pronome: z.string().trim().min(1, 'Campo obrigatório'),
    possuiApelido: z.boolean(),
    apelido: z.string().nullable(),
    tamanhoCamisa: z.string().trim().min(1, 'Campo obrigatório'),
    tamanhoCalcado: z.string().trim().min(1, 'Campo obrigatório'),
    possuiFilhos: z.boolean(),
    filhos: z.array(FilhoEtapa2Schema),
    possuiAlergia: z.boolean(),
    alergiaDescricao: z.string().nullable(),
    tipoAlimentacao: z.string().trim().min(1, 'Campo obrigatório'),
    possuiRestricaoAlimentar: z.boolean(),
    restricaoAlimentarDescricao: z.string().nullable(),
    comidaBebidaFavorita: z.string().trim().min(1, 'Campo obrigatório'),
    tempoLivre: z.string().trim().min(1, 'Campo obrigatório'),
    // Sem campo no perfil — opcionais (decisão do RH).
    lugarQuerVisitar: z.string().nullable().optional(),
    corFavorita: z.string().trim().min(1, 'Campo obrigatório'),
    livroFavorito: z.string().trim().min(1, 'Campo obrigatório'),
    filmeFavorito: z.string().trim().min(1, 'Campo obrigatório'),
    personagemFavorito: z.string().trim().min(1, 'Campo obrigatório'),
    bandaCantorFavorito: z.string().nullable().optional(),
    musicaDoMomento: z.string().nullable().optional(),
    praticaEsporte: z.string().trim().min(1, 'Campo obrigatório'),
    talentoEspecial: z.string().trim().min(1, 'Campo obrigatório'),
    metaSonho: z.string().trim().min(1, 'Campo obrigatório'),
    possuiPet: z.boolean(),
    petDescricao: z.string().nullable(),
    interesseCompartilharFotos: z.boolean(),
    autorizaUsoImagens: z.boolean().nullable(),
    autorizacaoTermo: z.literal(true, {
      errorMap: () => ({ message: 'É necessário concordar com o termo para continuar.' }),
    }),
    documentos: DocumentosEtapa2Schema,
  })
  .refine((d) => !d.possuiDeficiencia || !!d.deficienciaCid?.trim(), {
    message: 'Informe o CID da deficiência.',
    path: ['deficienciaCid'],
  })
  .refine((d) => !escolaridadeTemCurso(d.escolaridade) || !!d.escolaridadeCurso?.trim(), {
    message: 'Informe o curso.',
    path: ['escolaridadeCurso'],
  })
  .refine((d) => d.tipoContrato !== 'PJ' || !!d.cnpj?.trim(), {
    message: 'Informe o CNPJ.',
    path: ['cnpj'],
  })
  .refine((d) => d.tipoContrato !== 'PJ' || !!d.razaoSocial?.trim(), {
    message: 'Informe a razão social.',
    path: ['razaoSocial'],
  })
  .refine((d) => d.tipoContrato !== 'Estagiário' || !!d.estagioPeriodo?.trim(), {
    message: 'Informe o período do estágio.',
    path: ['estagioPeriodo'],
  })
  .refine((d) => d.tipoContrato !== 'Estagiário' || !!d.estagioInstituicao?.trim(), {
    message: 'Informe a instituição de ensino.',
    path: ['estagioInstituicao'],
  })
  .refine((d) => d.tipoContrato !== 'Estagiário' || !!d.escolaridadeCurso?.trim(), {
    message: 'Informe o curso.',
    path: ['escolaridadeCurso'],
  })
  // Com ou sem Itaú, todos informam uma conta completa de outro banco (decisão do RH).
  .refine((d) => !!d.outroBancoCodigo?.trim() && d.outroBancoCodigo.trim() !== BANCO_ITAU_CODIGO, {
    message: 'Informe um banco que não seja o Itaú.',
    path: ['outroBancoCodigo'],
  })
  .refine((d) => !!d.outroBancoDigito?.trim(), {
    message: 'Informe o dígito da conta.',
    path: ['outroBancoDigito'],
  })
  .refine((d) => !!d.outroBancoAgencia?.trim(), {
    message: 'Informe a agência.',
    path: ['outroBancoAgencia'],
  })
  .refine((d) => !!d.outroBancoConta?.trim(), {
    message: 'Informe a conta.',
    path: ['outroBancoConta'],
  })
  .refine((d) => !!d.outroBancoPix?.trim(), {
    message: 'Informe a chave Pix.',
    path: ['outroBancoPix'],
  })
  .refine((d) => d.regimeTrabalho.every((r) => r === 'Remoto') || !!d.linhaOnibusTarifa?.trim(), {
    message: 'Informe as linhas de ônibus e a tarifa.',
    path: ['linhaOnibusTarifa'],
  })
  .refine((d) => (d.estadoCivil !== 'Casado(a)' && d.estadoCivil !== 'União Estável') || !!d.nomeConjuge?.trim(), {
    message: 'Informe o nome do cônjuge.',
    path: ['nomeConjuge'],
  })
  .refine((d) => d.telefone.replace(/\D/g, '').length >= 12, {
    message: 'Informe um telefone válido, com DDD e número.',
    path: ['telefone'],
  })
  .refine((d) => d.contatoEmergenciaTelefone.replace(/\D/g, '').length >= 12, {
    message: 'Informe o telefone do contato de emergência completo, com DDD e número.',
    path: ['contatoEmergenciaTelefone'],
  })
  .refine((d) => !d.possuiApelido || !!d.apelido?.trim(), {
    message: 'Informe o apelido.',
    path: ['apelido'],
  })
  .refine((d) => !d.possuiFilhos || d.filhos.length > 0, {
    message: 'Informe ao menos um filho.',
    path: ['filhos'],
  })
  .refine((d) => !d.possuiAlergia || !!d.alergiaDescricao?.trim(), {
    message: 'Informe a alergia.',
    path: ['alergiaDescricao'],
  })
  .refine((d) => !d.possuiRestricaoAlimentar || !!d.restricaoAlimentarDescricao?.trim(), {
    message: 'Informe a restrição alimentar.',
    path: ['restricaoAlimentarDescricao'],
  })
  .refine((d) => !d.possuiPet || !!d.petDescricao?.trim(), {
    message: 'Informe qual animal e o nome dele.',
    path: ['petDescricao'],
  })
  .refine((d) => !d.interesseCompartilharFotos || d.autorizaUsoImagens !== null, {
    message: 'Informe se autoriza o uso das imagens.',
    path: ['autorizaUsoImagens'],
  });
